import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { confirmMatch as confirmMatchApi, declineMatch as declineMatchApi } from '../../api/gameService';
import socketService from '../../api/socketService';
import { useGame } from '../../hooks/useGame';
import './RematchBridge.css';

/**
 * R34cc — pont global pour les défis de revanche PvP.
 * - Écoute 'elementum:rematch-challenge' → modal « X vous défie ! » (accepter/refuser).
 * - Écoute 'elementum:match-confirmed' → bascule les deux joueurs vers le duel.
 * - Écoute 'elementum:match-cancelled' → ferme la modal (refus/délai).
 * Monté au niveau App : fonctionne depuis n'importe quel écran.
 */
const RematchBridge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { leaveGame } = useGame();
  const [challenge, setChallenge] = useState(null); // { gameId, opponent, series, deadline, stake }
  const [closing, setClosing] = useState(false);
  const [seconds, setSeconds] = useState(15);

  // compte à rebours du défi
  useEffect(() => {
    if (!challenge) return undefined;
    setSeconds(Math.max(0, Math.ceil((challenge.deadline - Date.now()) / 1000)));
    const iv = setInterval(() => {
      const left = Math.max(0, Math.ceil((challenge.deadline - Date.now()) / 1000));
      setSeconds(left);
      if (left <= 0) setChallenge(null);
    }, 1000);
    return () => clearInterval(iv);
  }, [challenge]);

  useEffect(() => {
    const onChallenge = (e) => setChallenge(e.detail || {});
    const onConfirmed = (e) => {
      const gid = e.detail && e.detail.gameId;
      if (!gid) return;
      // ne pas re-naviguer si déjà sur ce duel
      if (location.pathname === `/game/battle/${gid}`) return;
      setChallenge(null);
      // nettoyer l'ancienne partie (ex: écran de résultat du duel précédent)
      try { leaveGame(); } catch (err) { /* non bloquant */ }
      try { socketService.joinGame(gid); } catch (err) { /* non bloquant */ }
      navigate(`/game/battle/${gid}`);
    };
    const onCancelled = () => setChallenge(null);

    window.addEventListener('elementum:rematch-challenge', onChallenge);
    window.addEventListener('elementum:match-confirmed', onConfirmed);
    window.addEventListener('elementum:match-cancelled', onCancelled);
    return () => {
      window.removeEventListener('elementum:rematch-challenge', onChallenge);
      window.removeEventListener('elementum:match-confirmed', onConfirmed);
      window.removeEventListener('elementum:match-cancelled', onCancelled);
    };
  }, [navigate, location.pathname, leaveGame]);

  if (!challenge) return null;

  const respond = async (accept) => {
    const gid = challenge.gameId;
    setClosing(true);
    try {
      if (accept) {
        await confirmMatchApi(gid);
        // 'elementum:match-confirmed' déclenchera la navigation
      } else {
        await declineMatchApi(gid);
        setChallenge(null);
      }
    } catch (e) {
      setChallenge(null);
    } finally {
      setClosing(false);
    }
  };

  const s = challenge.series || {};

  return (
    <div className="rm-bridge-overlay">
      <div className="rm-bridge-modal">
        <div className="rm-bridge-crest">⚔️</div>
        <h3 className="rm-bridge-title">
          <b>{challenge.opponent || 'Un duelliste'}</b> vous défie !
        </h3>
        {s.gamesPlayed > 0 && (
          <div className="rm-bridge-score">
            Série : vous <b>{s.myWins ?? 0}</b> — <b>{s.oppWins ?? 0}</b> {challenge.opponent}
            <span className="rm-bridge-count">({s.gamesPlayed}/{s.maxGames || 3} duels)</span>
          </div>
        )}
        {challenge.stake > 0 && (
          <div className="rm-bridge-stake">Mise : {challenge.stake} 🪙 chacun</div>
        )}
        <div className="rm-bridge-timer">{seconds}s pour répondre</div>
        <div className="rm-bridge-actions">
          <button className="rm-btn rm-accept" disabled={closing} onClick={() => respond(true)}>
            Accepter le défi
          </button>
          <button className="rm-btn rm-decline" disabled={closing} onClick={() => respond(false)}>
            Refuser
          </button>
        </div>
      </div>
    </div>
  );
};

export default RematchBridge;
